/**
 * @file CorsProxyConfig.js
 * @description Configuración centralizada para el proxy CORS
 * @version 1.0.0
 * 
 * Este módulo implementa una solución robusta para manejar problemas CORS persistentes
 * con un enfoque modular que separa la configuración de la implementación.
 */

/**
 * @class CorsProxyConfig
 * @description Clase singleton para administrar la configuración del proxy CORS
 */
class CorsProxyConfig {
  constructor() {
    // Lista de dominios que siempre deben usar el proxy CORS
    this._alwaysUseProxyDomains = [
      'sync.nubelytics.com'
    ];
    
    // Lista de dominios seguros que nunca deben usar proxy
    this._safeDomains = [
      'localhost',
      '127.0.0.1',
      'nubelytics.web.app',
      'nubelytics.firebaseapp.com',
      'googleapis.com',
      'api.nubelytics.com',
      'us-central1-nubelytics.cloudfunctions.net',
      'nubelytics.com'
    ];

    // Configuración de cabeceras personalizadas que se deben preservar
    this._importantHeaders = [
      'x-monitor-check',
      'x-app-version',
      'authorization'
    ];

    // Configuración de diagnóstico
    this._diagnosticConfig = {
      enableTelemetry: true,
      storeMetrics: true,
      maxStoredRequests: 50,
      corsProxyEndpoint: process.env.REACT_APP_CORS_PROXY_ENDPOINT || '/proxy'
    };
    
    // Intentar recuperar dominios adicionales del localStorage
    this._initFromLocalStorage();
  }
  
  /**
   * Inicializa con datos guardados en localStorage si están disponibles
   * @private
   */
  _initFromLocalStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        // Recuperar dominios adicionales guardados
        const storedDomains = localStorage.getItem('nubelytics_proxy_domains');
        if (storedDomains) {
          const parsedDomains = JSON.parse(storedDomains);
          if (Array.isArray(parsedDomains)) {
            // Filtrar dominios válidos y añadir solo los que no existan ya
            parsedDomains.forEach(domain => {
              if (typeof domain === 'string' && domain.trim() && !this._alwaysUseProxyDomains.includes(domain)) {
                this._alwaysUseProxyDomains.push(domain);
              }
            });
          }
        }
        
        // Recuperar dominios seguros guardados
        const storedSafeDomains = localStorage.getItem('nubelytics_safe_domains');
        if (storedSafeDomains) {
          const parsedSafeDomains = JSON.parse(storedSafeDomains);
          if (Array.isArray(parsedSafeDomains)) {
            // Filtrar dominios válidos y añadir solo los que no existan ya
            parsedSafeDomains.forEach(domain => {
              if (typeof domain === 'string' && domain.trim() && !this._safeDomains.includes(domain)) {
                this._safeDomains.push(domain);
              }
            });
          }
        }
      }
    } catch (e) {
      console.warn('[CorsProxyConfig] Error al recuperar configuración del localStorage:', e);
    }
  }
  
  /**
   * Guarda los dominios actuales en localStorage
   * @private
   */
  _saveToLocalStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('nubelytics_proxy_domains', JSON.stringify(this._alwaysUseProxyDomains));
        localStorage.setItem('nubelytics_safe_domains', JSON.stringify(this._safeDomains));
      }
    } catch (e) {
      console.warn('[CorsProxyConfig] Error al guardar configuración en localStorage:', e);
    }
  }
  
  /**
   * Obtiene la lista de dominios que siempre usan proxy
   * @readonly
   * @returns {Array<string>} Lista de dominios
   */
  get ALWAYS_USE_PROXY_DOMAINS() {
    return [...this._alwaysUseProxyDomains]; // Devolver copia para evitar modificaciones directas
  }
  
  /**
   * Obtiene la lista de cabeceras importantes
   * @readonly
   * @returns {Array<string>} Lista de cabeceras
   */
  get IMPORTANT_HEADERS() {
    return [...this._importantHeaders];
  }
  
  /**
   * Obtiene la configuración de diagnóstico
   * @readonly
   * @returns {Object} Configuración de diagnóstico
   */
  get DIAGNOSTIC_CONFIG() {
    return { ...this._diagnosticConfig };
  }
  
  /**
   * Añade un dominio a la lista de dominios que siempre usan proxy
   * @param {string} domain - Dominio a añadir (ej. 'api.example.com')
   * @returns {boolean} true si se añadió correctamente
   */
  addDomainToForceList(domain) {
    if (!domain || typeof domain !== 'string' || domain.trim() === '') {
      return false;
    }
    
    // Normalizar el dominio
    const normalizedDomain = domain.trim().toLowerCase();
    
    // Verificar si ya existe
    if (this._alwaysUseProxyDomains.includes(normalizedDomain)) {
      return false;
    }
    
    // Añadir a la lista
    this._alwaysUseProxyDomains.push(normalizedDomain);
    
    // Guardar en localStorage
    this._saveToLocalStorage();
    
    console.info(`[CorsProxyConfig] Añadido '${normalizedDomain}' a la lista de dominios con proxy forzado`);
    return true;
  }
  
  /**
   * Elimina un dominio de la lista de dominios que siempre usan proxy
   * @param {string} domain - Dominio a eliminar
   * @returns {boolean} true si se eliminó correctamente
   */
  removeDomainFromForceList(domain) {
    if (!domain || typeof domain !== 'string') {
      return false;
    }
    
    const normalizedDomain = domain.trim().toLowerCase();
    const initialLength = this._alwaysUseProxyDomains.length;
    
    this._alwaysUseProxyDomains = this._alwaysUseProxyDomains.filter(d => d !== normalizedDomain);
    
    if (this._alwaysUseProxyDomains.length !== initialLength) {
      // Guardar cambios
      this._saveToLocalStorage();
      console.info(`[CorsProxyConfig] Eliminado '${normalizedDomain}' de la lista de dominios con proxy forzado`);
      return true;
    }
    
    return false;
  }
  
  /**
   * Añade un dominio a la lista de dominios seguros que nunca deben usar proxy
   * @param {string} domain - Dominio a añadir (ej. 'auth.nubelytics.com')
   * @returns {boolean} true si se añadió correctamente
   */
  addSafeDomain(domain) {
    if (!domain || typeof domain !== 'string' || domain.trim() === '') {
      return false;
    }
    
    // Normalizar el dominio
    const normalizedDomain = domain.trim().toLowerCase();
    
    // Verificar si ya existe
    if (this._safeDomains.includes(normalizedDomain)) {
      return false;
    }
    
    // Añadir a la lista
    this._safeDomains.push(normalizedDomain);
    
    // Guardar en localStorage
    this._saveToLocalStorage();
    
    console.info(`[CorsProxyConfig] Añadido '${normalizedDomain}' a la lista de dominios seguros`);
    return true;
  }
  
  /**
   * Elimina un dominio de la lista de dominios seguros
   * @param {string} domain - Dominio a eliminar
   * @returns {boolean} true si se eliminó correctamente
   */
  removeSafeDomain(domain) {
    if (!domain || typeof domain !== 'string') {
      return false;
    }
    
    const normalizedDomain = domain.trim().toLowerCase();
    const initialLength = this._safeDomains.length;
    
    this._safeDomains = this._safeDomains.filter(d => d !== normalizedDomain);
    
    if (this._safeDomains.length !== initialLength) {
      // Guardar cambios
      this._saveToLocalStorage();
      console.info(`[CorsProxyConfig] Eliminado '${normalizedDomain}' de la lista de dominios seguros`);
      return true;
    }
    
    return false;
  }
  
  /**
   * Verifica si un dominio es seguro y no necesita proxy
   * @param {string} url - URL a verificar
   * @returns {boolean} true si es un dominio seguro
   */
  isSafeDomain(url) {
    try {
      if (!url) return true;
      const urlObj = new URL(url);
      return this._safeDomains.some(
        domain => urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
      );
    } catch (err) {
      if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        console.error('[CorsProxyConfig] Error isSafeDomain:', err?.message || err);
      }
      return false;
    }
  }
  
  /**
   * Determina si una URL debe usar siempre el proxy CORS
   * @param {string} url - URL a verificar
   * @returns {boolean} true si debe usar proxy
   */
  shouldAlwaysUseProxy(url) {
    // Delegar a implementación parcheada
    try {
      if (!url) return false;
      // Si es dominio seguro => no proxy
      if (this.isSafeDomain(url)) return false;
      const urlObj = new URL(url);
      return this._alwaysUseProxyDomains.some(domain =>
        urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
      );
    } catch (err) {
      if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        console.error('[CorsProxyConfig] Error shouldAlwaysUseProxy:', err?.message || err);
      }
      return false;
    }
  }
  // wrapper end of shouldAlwaysUseProxy

  /**
   * Genera la URL del proxy CORS para una URL objetivo.
   * Evita reproxificar si ya está proxificada.
   * @param {string} targetUrl - URL destino final.
   * @returns {string} URL para invocar vía proxy.
   */
  generateProxyUrl(targetUrl) {
    if (!targetUrl) return targetUrl;
    let proxyEndpoint = this._diagnosticConfig.corsProxyEndpoint;
    // Si es ruta relativa, resolver contra window.location.origin para evitar mezcla con baseURL de axios
    if (proxyEndpoint && proxyEndpoint.startsWith('/') && typeof window !== 'undefined' && window.location) {
      proxyEndpoint = `${window.location.origin}${proxyEndpoint}`;
    }
    try {
      if (targetUrl.startsWith(proxyEndpoint)) {
        return targetUrl;
      }
      const decodedOnce = decodeURIComponent(targetUrl);
      if (decodedOnce.startsWith(proxyEndpoint)) {
        return targetUrl;
      }
    } catch (e) {
      // ignorar errores de decodificación
    }
    const encodedUrl = encodeURIComponent(targetUrl);
    return `${proxyEndpoint}?url=${encodedUrl}`;
  }
  
  /**
   * Devuelve el endpoint del proxy resuelto a URL absoluta.
   * @returns {string}
   */
  getResolvedProxyEndpoint() {
    let proxyEndpoint = this._diagnosticConfig.corsProxyEndpoint;
    if (proxyEndpoint && proxyEndpoint.startsWith('/') && typeof window !== 'undefined' && window.location) {
      proxyEndpoint = `${window.location.origin}${proxyEndpoint}`;
    }
    return proxyEndpoint;
  
  }

}

  /*

  /**
  try {
    // Si la URL ya apunta al endpoint del proxy, evitar reproxificar
    if (targetUrl.startsWith(proxyEndpoint)) {
      return targetUrl;
    const urlObj = new URL(url);
    return this._safeDomains.some((domain) =>
      urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
    );
  } catch (err) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.error('[CorsProxyConfig] Error isSafeDomain:', err?.message || err);
    }
    return false; // URL inválida => no segura para proxy
  }
*/

// --- LEGACY PATCHES ELIMINADOS ---
/*
CorsProxyConfig.prototype.shouldAlwaysUseProxy = function (url) {
  const ctx = this && this._alwaysUseProxyDomains ? this : corsProxyConfig;
  if (!url) return false;
  try {
    let safe = false;
    if (typeof ctx.isSafeDomain === 'function') {
      safe = ctx.isSafeDomain(url);
    } else if (Array.isArray(ctx._safeDomains)) {
      const urlObj = new URL(url);
      safe = ctx._safeDomains.some((domain) =>
        urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
      );
    }
    if (safe) return false;

    const urlObj = new URL(url);
    return ctx._alwaysUseProxyDomains.some((domain) =>
      urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
    );
  } catch (err) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.error('[CorsProxyConfig] Error shouldAlwaysUseProxy:', err?.message || err);
    }
    return false;
  }
};
*/

// Garantizar stub global para compatibilidad temprana
if (typeof window !== 'undefined') {
  window.EK = window.EK || {};
  if (!window.EK.corsRegistry) {
    window.EK.corsRegistry = { runDiagnosticTest: () => false };
  } else if (typeof window.EK.corsRegistry.runDiagnosticTest !== 'function') {
    window.EK.corsRegistry.runDiagnosticTest = () => false;
  }
}

// Crear instancia singleton
const corsProxyConfig = new CorsProxyConfig();

// Exportar como singleton y métodos individuales para compatibilidad
export const { 
  ALWAYS_USE_PROXY_DOMAINS, 
  IMPORTANT_HEADERS, 
  DIAGNOSTIC_CONFIG 
} = corsProxyConfig;

export const shouldAlwaysUseProxy = corsProxyConfig.shouldAlwaysUseProxy.bind(corsProxyConfig);
export const generateProxyUrl = corsProxyConfig.generateProxyUrl.bind(corsProxyConfig);
export const addDomainToForceList = corsProxyConfig.addDomainToForceList.bind(corsProxyConfig);
export const removeDomainFromForceList = corsProxyConfig.removeDomainFromForceList.bind(corsProxyConfig);
export const addSafeDomain = corsProxyConfig.addSafeDomain.bind(corsProxyConfig);
export const removeSafeDomain = corsProxyConfig.removeSafeDomain.bind(corsProxyConfig);
export const isSafeDomain = corsProxyConfig.isSafeDomain.bind(corsProxyConfig);

export default corsProxyConfig;
